import { useEffect, useMemo, useState } from "react";
import Button from "../../../shared/components/Button";
import OrganizerDatePicker, {
  formatDateInputValue,
  parseDateInputValue,
} from "../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput from "../../../shared/components/OrganizerTimeInput";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import type {
  AdminAvailabilityRestrictionPayload,
  AdminEventRegistration,
} from "../services/eventRegistrationAdminService";

type Props = {
  registration: AdminEventRegistration;
  saving: boolean;
  canRemove: boolean;
  onClose: () => void;
  onSubmit: (
    payload: AdminAvailabilityRestrictionPayload,
  ) => Promise<{ impactedCount?: number; lockedCount?: number } | void>;
};

type PeriodPreset = {
  id: string;
  label: string;
  startTime: string;
  endTime: string;
  type: "full_day" | "day_period" | "time_range";
};

type AvailabilityRestrictionDetail = NonNullable<
  NonNullable<AdminEventRegistration["availabilityRestriction"]>["details"]
>[number];

const PERIOD_PRESETS: PeriodPreset[] = [
  {
    id: "full_day",
    label: "Dia todo",
    startTime: "00:00",
    endTime: "23:59",
    type: "full_day",
  },
  {
    id: "morning",
    label: "Manhã",
    startTime: "08:00",
    endTime: "12:00",
    type: "day_period",
  },
  {
    id: "afternoon",
    label: "Tarde",
    startTime: "12:00",
    endTime: "18:00",
    type: "day_period",
  },
  {
    id: "night",
    label: "Noite",
    startTime: "18:00",
    endTime: "23:59",
    type: "day_period",
  },
  {
    id: "custom",
    label: "Intervalo específico",
    startTime: "08:00",
    endTime: "10:00",
    type: "time_range",
  },
];

function getPeriodPreset(id: string) {
  return PERIOD_PRESETS.find((item) => item.id === id) || PERIOD_PRESETS[0];
}

const DEFAULT_PERIOD_PRESET = getPeriodPreset("morning");

function getPresetIdFromRestriction(restriction: AvailabilityRestrictionDetail) {
  if (restriction.type === "full_day") return "full_day";
  if (restriction.type === "time_range") return "custom";
  const start = getTimeValue(restriction.startAt);
  const end = getTimeValue(restriction.endAt);
  const matchingPreset = PERIOD_PRESETS.find(
    (preset) =>
      preset.type === "day_period" &&
      preset.startTime === start &&
      preset.endTime === end,
  );
  return matchingPreset?.id || "custom";
}

function getLatestRestrictionReason(restriction: AvailabilityRestrictionDetail) {
  const audit = Array.isArray(restriction.audit) ? restriction.audit : [];
  for (let index = audit.length - 1; index >= 0; index -= 1) {
    const reason = String(audit[index]?.reason || "").trim();
    if (reason) return reason;
  }
  return "";
}

function getTimeValue(value?: string | null) {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;
}

function buildDateTimeIso(dateValue: string, timeValue: string) {
  const date = parseDateInputValue(dateValue);
  if (!date) return "";
  const [hoursRaw, minutesRaw] = String(timeValue || "00:00").split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return "";
  }
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0,
  ).toISOString();
}

function formatWeekday(dateValue: string) {
  const date = parseDateInputValue(dateValue);
  if (!date) return "";
  return new Intl.DateTimeFormat("pt-BR", { weekday: "long" }).format(date);
}

function buildRestrictionLabel({
  dateValue,
  preset,
  startTime,
  endTime,
}: {
  dateValue: string;
  preset: PeriodPreset;
  startTime: string;
  endTime: string;
}) {
  const weekday = formatWeekday(dateValue);
  if (!weekday) return "Restrição cadastrada pelo administrador";
  if (preset.id === "full_day") return `Não jogar ${weekday} o dia todo`;
  if (preset.id === "morning") return `Não jogar ${weekday} pela manhã`;
  if (preset.id === "afternoon") return `Não jogar ${weekday} à tarde`;
  if (preset.id === "night") return `Não jogar ${weekday} à noite`;
  return `Não jogar ${weekday} das ${startTime} às ${endTime}`;
}

function centsFromCurrencyInput(value: string) {
  const parsed = Number(String(value || "0").replace(/\./g, "").replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed * 100);
}

function formatRestrictionPeriod(restriction: AvailabilityRestrictionDetail) {
  const start = new Date(restriction.startAt);
  const end = new Date(restriction.endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "Período não informado";
  }
  const dateLabel = new Intl.DateTimeFormat("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(start);
  const timeFormatter = new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${dateLabel} · ${timeFormatter.format(start)} às ${timeFormatter.format(end)}`;
}

export default function AdminAvailabilityRestrictionModal({
  registration,
  saving,
  canRemove,
  onClose,
  onSubmit,
}: Props) {
  useBodyScrollLock(true);

  const restrictions = useMemo(
    () => registration.availabilityRestriction?.details || [],
    [registration.availabilityRestriction?.details],
  );
  const [editingRestrictionId, setEditingRestrictionId] = useState<number | null>(null);
  const current = useMemo(
    () =>
      restrictions.find(
        (restriction) => Number(restriction.id) === Number(editingRestrictionId),
      ) || null,
    [editingRestrictionId, restrictions],
  );
  const [dateValue, setDateValue] = useState("");
  const [periodPresetId, setPeriodPresetId] = useState(DEFAULT_PERIOD_PRESET.id);
  const [startTime, setStartTime] = useState(DEFAULT_PERIOD_PRESET.startTime);
  const [endTime, setEndTime] = useState(DEFAULT_PERIOD_PRESET.endTime);
  const [amount, setAmount] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<
    "not_required" | "pending" | "confirmed"
  >("not_required");
  const [reason, setReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    if (!current) {
      setDateValue("");
      setPeriodPresetId(DEFAULT_PERIOD_PRESET.id);
      setStartTime(DEFAULT_PERIOD_PRESET.startTime);
      setEndTime(DEFAULT_PERIOD_PRESET.endTime);
      setAmount("");
      setPaymentStatus("not_required");
      setReason("");
      setAdminNotes("");
      return;
    }
    const nextPresetId = getPresetIdFromRestriction(current);
    const nextPreset = getPeriodPreset(nextPresetId);
    setDateValue(current.startAt ? formatDateInputValue(new Date(current.startAt)) : "");
    setPeriodPresetId(nextPreset.id);
    setStartTime(getTimeValue(current.startAt) || nextPreset.startTime);
    setEndTime(getTimeValue(current.endAt) || nextPreset.endTime);
    setAmount(current.amountCents ? String((current.amountCents / 100).toFixed(2)) : "");
    setPaymentStatus(
      current.paymentStatus === "confirmed"
        ? "confirmed"
        : current.paymentStatus === "pending"
          ? "pending"
          : "not_required",
    );
    setReason(getLatestRestrictionReason(current));
    setAdminNotes(current.adminNotes || "");
  }, [current]);

  const preset = getPeriodPreset(periodPresetId);
  const effectiveStartTime = preset.id === "custom" ? startTime : preset.startTime;
  const effectiveEndTime = preset.id === "custom" ? endTime : preset.endTime;
  const previewLabel = useMemo(
    () =>
      buildRestrictionLabel({
        dateValue,
        preset,
        startTime: effectiveStartTime,
        endTime: effectiveEndTime,
      }),
    [dateValue, effectiveEndTime, effectiveStartTime, preset],
  );

  const buildPayload = (mode: "impact" | "upsert" | "remove") => {
    const payloadReason =
      reason.trim() || (current && mode === "upsert" ? "Atualização administrativa" : "");
    if (mode === "remove") {
      return {
        mode,
        restrictionId: current?.id,
        reason: payloadReason,
        adminNotes,
      } satisfies AdminAvailabilityRestrictionPayload;
    }
    return {
      mode,
      restrictionId: current?.id,
      type: preset.type,
      label: previewLabel,
      startAt: buildDateTimeIso(dateValue, effectiveStartTime),
      endAt: buildDateTimeIso(dateValue, effectiveEndTime),
      amountCents: paymentStatus === "not_required" ? 0 : centsFromCurrencyInput(amount),
      paymentStatus,
      reason: payloadReason,
      adminNotes,
    } satisfies AdminAvailabilityRestrictionPayload;
  };

  const submit = async (mode: "upsert" | "remove") => {
    setError("");
    if (!reason.trim() && (!current || mode === "remove")) {
      setError("Informe o motivo da alteração.");
      return;
    }
    if (mode === "upsert" && !dateValue) {
      setError("Informe o dia da restrição.");
      return;
    }
    const impact = await onSubmit(buildPayload("impact"));
    const impactResult = impact && typeof impact === "object" ? impact : {};
    const impactedCount = impactResult.impactedCount || 0;
    const lockedCount = impactResult.lockedCount || 0;
    if (impactedCount > 0) {
      const lockedMessage = lockedCount
        ? ` ${lockedCount} partida(s) já iniciada(s) ou encerrada(s) não poderão ser alteradas automaticamente.`
        : "";
      const confirmed = window.confirm(
        `Esta alteração impactará ${impactedCount} partida(s) já agendada(s).${lockedMessage} Deseja continuar?`,
      );
      if (!confirmed) return;
    }
    await onSubmit(buildPayload(mode));
  };

  return (
    <div
      className="admin-modal-backdrop admin-nested-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Adicionar restrição"
    >
      <div className="admin-modal admin-availability-restriction-modal">
        <div className="admin-modal-header">
          <div>
            <h3>{current ? "Editar restrição" : "Adicionar restrição"}</h3>
            <p>{registration.userName || "Atleta"} · {registration.partnerName || "Individual"}</p>
          </div>
          <Button type="button" variant="pill" onClick={onClose} disabled={saving}>
            Fechar
          </Button>
        </div>

        {restrictions.length > 0 && (
          <div className="admin-availability-restriction-list">
            <div className="admin-availability-restriction-list-header">
              <strong>Restrições cadastradas</strong>
              <Button
                type="button"
                variant="pill"
                onClick={() => setEditingRestrictionId(null)}
                disabled={saving || !current}
              >
                Nova restrição
              </Button>
            </div>
            {restrictions.map((restriction) => {
              const active = current?.id === restriction.id;
              return (
                <div
                  key={restriction.id}
                  className={`admin-availability-restriction-item${
                    active ? " is-active" : ""
                  }`}
                >
                  <div>
                    <strong>{restriction.label || "Restrição cadastrada"}</strong>
                    <span>{formatRestrictionPeriod(restriction)}</span>
                  </div>
                  <Button
                    type="button"
                    variant="pill"
                    onClick={() => setEditingRestrictionId(restriction.id)}
                    disabled={saving || active}
                  >
                    Editar
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="admin-form-grid admin-availability-restriction-grid">
          <label className="field">
            <span>Tipo da restrição</span>
            <select
              value={periodPresetId}
              onChange={(event) => {
                const nextPreset = getPeriodPreset(event.target.value);
                setPeriodPresetId(nextPreset.id);
                setStartTime(nextPreset.startTime);
                setEndTime(nextPreset.endTime);
              }}
            >
              {PERIOD_PRESETS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <OrganizerDatePicker
            label="Dia"
            value={dateValue}
            onChange={setDateValue}
          />

          {preset.id === "custom" && (
            <>
              <OrganizerTimeInput
                label="Horário inicial"
                value={startTime}
                onChange={setStartTime}
              />
              <OrganizerTimeInput
                label="Horário final"
                value={endTime}
                onChange={setEndTime}
              />
            </>
          )}

          <label className="field">
            <span>Status da cobrança</span>
            <select
              value={paymentStatus}
              onChange={(event) =>
                setPaymentStatus(
                  event.target.value as "not_required" | "pending" | "confirmed",
                )
              }
            >
              <option value="not_required">Não cobrar / isento</option>
              <option value="pending">Pendente</option>
              <option value="confirmed">Confirmado</option>
            </select>
          </label>

          <label className="field">
            <span>Valor adicional</span>
            <input
              type="text"
              value={paymentStatus === "not_required" ? "" : amount}
              disabled={paymentStatus === "not_required"}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="0,00"
            />
          </label>

          <label className="field admin-availability-restriction-wide">
            <span>Motivo da inclusão/alteração</span>
            <input
              type="text"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Ex.: solicitação recebida via WhatsApp"
            />
          </label>

          <label className="field admin-availability-restriction-wide">
            <span>Observações administrativas</span>
            <textarea
              value={adminNotes}
              onChange={(event) => setAdminNotes(event.target.value)}
              rows={3}
            />
          </label>
        </div>

        <div className="admin-availability-restriction-preview">
          <strong>{previewLabel}</strong>
          <span>
            {paymentStatus === "not_required"
              ? "Cobrança isenta"
              : paymentStatus === "confirmed"
                ? "Cobrança confirmada"
                : "Cobrança pendente"}
          </span>
        </div>

        {error && <p className="status-message error">{error}</p>}

        <div className="admin-availability-restriction-actions">
          {current && canRemove && (
            <Button
              type="button"
              variant="pill"
              onClick={() => void submit("remove")}
              disabled={saving}
            >
              Remover restrição
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            onClick={() => void submit("upsert")}
            disabled={saving}
          >
            {saving ? "Salvando..." : current ? "Salvar restrição" : "Adicionar restrição"}
          </Button>
        </div>
      </div>
    </div>
  );
}
